import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { formatPrice, effectivePrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORIES = ["all", "caffe", "dolci", "aperitivi", "bevande"];

export function MenuGrid({ menu, onAdd }) {
  const { t, lang } = useLanguage();
  const [cat, setCat] = useState("all");
  const promo = menu.find((m) => m.promo_price != null && m.available);
  const items = (cat === "all" ? menu : menu.filter((m) => m.category === cat)).filter((m) => m !== promo);

  return (
    <div>
      {promo && (
        <div data-testid="promo-banner" className="mb-5 bg-[#4A2C2A] text-[#FDFBF7] rounded-2xl p-4 flex items-center gap-4 shadow-lg">
          {promo.image && <img src={promo.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-[#F4A261] text-xs uppercase tracking-widest font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {t("promo_of_day")}</p>
            <h3 className="font-heading font-bold text-xl truncate">{lang === "it" ? promo.name_it : promo.name_en}</h3>
            <p className="text-sm">
              <span className="line-through text-[#F5EFE6]/60 mr-2">{formatPrice(promo.price)}</span>
              <span className="font-bold text-[#F4A261] text-lg" data-testid="promo-price">{formatPrice(promo.promo_price)}</span>
            </p>
          </div>
          <Button data-testid={`add-to-cart-${promo.item_id}`} onClick={() => onAdd(promo)} size="sm" className="rounded-full bg-[#D97706] hover:bg-[#b45309] text-white gap-1 shrink-0">
            <Plus className="w-4 h-4" /> {t("add_to_cart")}
          </Button>
        </div>
      )}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="category-filter">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`category-${c}`}
            onClick={() => setCat(c)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors border ${
              cat === c
                ? "bg-[#4A2C2A] text-[#FDFBF7] border-[#4A2C2A]"
                : "bg-white text-[#786C66] border-[#E8DFD5] hover:border-[#D97706]/50"
            }`}
          >
            {t(`cat_${c}`)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-5">
        {items.map((item, i) => (
          <motion.div
            key={item.item_id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.03 }}
            data-testid={`menu-item-${item.item_id}`}
            className={`bg-white border border-[#E8DFD5] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.12)] transition-shadow duration-300 ${!item.available ? "opacity-50" : ""}`}
          >
            {item.image && (
              <div className="h-36 overflow-hidden">
                <img src={item.image} alt={item.name_it} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-lg text-[#1F1615] truncate">
                    {lang === "it" ? item.name_it : item.name_en}
                  </h3>
                  <p className="text-sm text-[#786C66] line-clamp-2 mt-0.5">
                    {lang === "it" ? item.desc_it : item.desc_en}
                  </p>
                </div>
                <span className="font-heading font-bold text-[#D97706] whitespace-nowrap">{formatPrice(effectivePrice(item))}</span>
              </div>
              <Button
                data-testid={`add-to-cart-${item.item_id}`}
                disabled={!item.available}
                onClick={() => onAdd(item)}
                size="sm"
                className="mt-3 w-full rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] gap-1 transition-transform hover:scale-[1.02]"
              >
                <Plus className="w-4 h-4" /> {t("add_to_cart")}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
